// lib/arrivalsDayView.mjs — R114 Arrivals "Day" view: the pure client-side
// helpers ResTrackerPage.ts runs (date math, sessionStorage parsing, the
// Check-ins / Check-outs / Both lists, labels). Plain ESM so
// `node lib/arrivalsDayView.test.mjs` tests the exact code the page runs
// (the eodFeed pattern); types live in arrivalsDayView.d.mts.

// sessionStorage keys — HubPage re-instantiates the page on every hub render
// and on the 15-min auto-refresh, so the day / filter / view live here.
export const STORE_KEYS = { day: 'rt_day', mode: 'rt_day_mode', view: 'rt_view', focus: 'rt_focus' };
export const MODES = ['in', 'out', 'both'];
export const DEFAULT_MODE = 'both';
export const VIEWS = ['day', 'board'];
export const DEFAULT_VIEW = 'day';
export const DAY_WINDOW = 180;   // the server's ±180-day window (GET /day 400s outside it)

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
export function isDay(s) {
  if (typeof s !== 'string' || !DAY_RE.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}
// UTC-anchored on purpose: a calendar date, not an instant — so DST never
// skips or repeats a day.
export function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + Number(n || 0));
  return d.toISOString().slice(0, 10);
}
export function diffDays(a, b) {
  return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);
}
export function inWindow(day, today, windowDays = DAY_WINDOW) {
  if (!isDay(day) || !isDay(today)) return false;
  return Math.abs(diffDays(today, day)) <= windowDays;
}
// A stored day: kept when it is a real day inside the window around `today`;
// garbage / empty / stale (out of window) → today.
export function parseStoredDay(raw, today) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (isDay(s) && (!isDay(today) || inWindow(s, today))) return s;
  return today;
}
// R114 review: the Day view's <input type=date>. Chromium / Safari fire
// `change` on EVERY keystroke while a date is typed ("2" of "21" → the 2nd);
// committing there redrew the page mid-entry (focus lost) or snapped a
// half-typed year back. This guard commits typed entry only on Enter / blur;
// a pick from the calendar popup (pointer, no keys) still commits on change.
// Each handler answers true = commit now (the page's commit() is a no-op when
// the value already is the shown day, so an extra true is harmless).
// R114 fix2: a pointerdown (e.g. clicking the month segment after typing the
// day) ends the keystroke run but must NOT drop the typed entry — `dirty`
// keeps it pending until Enter / blur commits it.
export function dateEntryGuard() {
  let typing = false;   // inside a keystroke run: change events are mid-entry
  let dirty = false;    // a typed entry not committed yet
  return {
    keydown(key) {
      if (key === 'Enter') { typing = false; dirty = false; return true; }
      if (key !== 'Tab') { typing = true; dirty = true; }
      return false;
    },
    pointerdown() { typing = false; return false; },
    change() { return !typing; },
    blur() { const go = typing || dirty; typing = false; dirty = false; return go; },
    get typing() { return typing; },
    get dirty() { return dirty; },
  };
}
// R114 review: one page instance holds BOTH views' data. A write made in one
// view leaves the other view's copy stale (staleAfterWrite names it); a switch
// to a view refetches it when it is missing, stale or (Day) for another date.
export function staleAfterWrite(activeView) {
  return parseView(activeView) === 'day' ? 'board' : 'day';
}
export function needsRefetch(target, s) {
  const x = s || {};
  if (parseView(target) === 'board') return !x.hasBoard || !!x.boardStale;
  return !x.hasDay || !!x.dayStale || x.dayDate !== x.shownDay;
}
// R114 fix2: per-view staleness with an invalidation GENERATION. A write in
// one view invalidates the other (gen + 1, stale). A fetch remembers the gen
// it started under (begin) and may clear staleness only if no write
// invalidated the view meanwhile (settle) — a /board request that started
// before a Day-view note save and answered after it must not mark the board
// fresh. A FAILED fetch never clears it: the cached copy stays stale (note
// boxes locked, an error bar with Retry) instead of quietly editable.
export function viewFreshness() {
  const st = { board: { gen: 0, stale: false }, day: { gen: 0, stale: false } };
  const k = (view) => (parseView(view) === 'board' ? 'board' : 'day');
  return {
    invalidate(view) { const x = st[k(view)]; x.gen += 1; x.stale = true; },
    begin(view) { return st[k(view)].gen; },
    settle(view, gen, ok) { const x = st[k(view)]; if (ok && gen === x.gen) x.stale = false; return x.stale; },
    isStale(view) { return st[k(view)].stale; },
  };
}
// The check-level note box. Locked only while the view on screen is STALE
// (another view wrote since this copy was fetched) — an ordinary reload after
// a write in this same view does not lock it (the saved note is patched into
// the copy at once). The label says why it is locked; "Saving…" is only ever
// the note actually being saved.
export function noteLock(o) {
  const x = o || {};
  if (x.saving) return { disabled: true, label: 'Saving…', why: '' };
  if (x.stale) return x.loading
    ? { disabled: true, label: 'Refreshing…', why: 'Loading the latest copy — notes unlock when it arrives.' }
    : { disabled: true, label: '🔒 Reload to edit', why: 'This copy may be out of date (it was changed in the other view and could not be reloaded) — reload before editing the note.' };
  return { disabled: false, label: '📝 Save note', why: '' };
}

// ── R114 fix2: pre-arrival on the Day view ───────────────────────────────
// The KPI tile text. A PAST day is history ("not ticked", never red, never
// "arrive in <48h"); arrivals with no checklist are UNKNOWN, never "sent".
export function paTileText(o) {
  const x = o || {};
  const n48 = x.past ? 0 : (Number(x.pending_48h) || 0);
  const det = Number(x.detected) || 0;
  const un = Number(x.untracked) || 0;
  const label = x.past ? 'Pre-arrival not ticked' : 'Pre-arrival not sent';
  const sub = x.filterOn ? 'filter on · tap to clear'
    : x.past ? 'past day · never ticked'
      : n48 ? `${n48} arrive in <48h`
        : det ? `${det} detected · confirm`
          : un ? `${un} without a checklist`
            : 'tap to filter';
  return { label, sub, red: n48 > 0 };
}
// The Day view's filtered ("Pre-arrival not sent") list — arrivals only.
export function paRowsFor(payload) {
  const ins = (payload && Array.isArray(payload.arrivals)) ? payload.arrivals : [];
  return ins.filter(a => a && a.check && a.check.prearrival && a.check.prearrival.state === 'pending');
}
// The filtered list's empty state: an all-clear ONLY when every arrival's
// pre-arrival status is actually known.
export function paEmptyText(payload, longLabel) {
  const p = payload || {};
  const day = longLabel || 'this day';
  const pa = p.prearrival || {};
  if ((p.unread || []).includes('rt_checks') || pa.pending == null) return `Checklists couldn’t be read just now — can’t tell which arrivals on ${day} still need their pre-arrival message. Retry, or clear the filter.`;
  const un = Number(pa.untracked) || 0;
  if (un) return `No checklist on ${day} is waiting on its pre-arrival message — but ${un} arrival${un === 1 ? ' has' : 's have'} no checklist yet, so ${un === 1 ? 'its' : 'their'} pre-arrival status is unknown.`;
  if (!((p.arrivals || []).length)) return `No check-ins on ${day}`;
  return pa.past ? `Every arrival on ${day} had its pre-arrival info ticked ✓` : `Every arrival on ${day} has its pre-arrival info sent ✓`;
}

// ── R114 fix2: the self-heal timer as the Day view sees it ────────────────
// Will the timer resync this day's checklists on its own? Only when it is
// armed on this server and the day is inside its coverage (today … +days).
export function autoSyncCovers(selfHeal, day, today) {
  const sh = selfHeal || {};
  if (!sh.armed || !isDay(day) || !isDay(today) || day < today) return false;
  return diffDays(today, day) <= (Number(sh.days) || 45);
}
// The "📅 Dates changed" tooltip — names the field that moved and only
// promises an automatic resync when one will run.
export function datesChangedTip(check, o) {
  const c = check || {};
  const x = o || {};
  const fields = Array.isArray(c.stale_fields) && c.stale_fields.length ? c.stale_fields : ['check_in'];
  const parts = [];
  if (fields.includes('check_in') && c.stored_check_in) parts.push(`check-in: the checklist still says ${longDay(c.stored_check_in)}, the booking now arrives ${longDay(c.check_in)}`);
  if (fields.includes('check_out') && c.stored_check_out) parts.push(`check-out: the checklist still says ${longDay(c.stored_check_out)}, the booking now leaves ${longDay(c.check_out)}`);
  const what = parts.length ? ` (${parts.join('; ')})` : '';
  const how = x.auto ? `the checklist itself resyncs automatically within ${Number(x.minutes) || 30} min` : 'tap ↻ Sync to update the checklist';
  return `The booking's dates changed after this checklist was made${what}. Deadlines here already use the new dates; ${how}.`;
}
// What to store for a chosen day: null = "follow today" (a tab left open
// overnight must land on the NEW today, not yesterday).
export function dayToStore(day, today) {
  return !isDay(day) || day === today ? null : day;
}
// R114 review: the day a NEW page instance restores (the hub re-creates the
// page on every render and on the 15-min refresh). The device's date can be a
// day off the business timezone (an offshore remote-ops laptop at 9 AM is
// already "tomorrow"), so the stored day is never collapsed against it —
// only kept or dropped by the window (one day of margin so the server's
// ±DAY_WINDOW check never 400s). Garbage / empty / out of window → null
// ("follow today"), never the device's date pinned as an explicit day.
// The collapse to "follow today" happens against payload.today instead
// (collapseToToday, once /day has answered).
export function restoreDay(raw, deviceToday) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!isDay(s)) return null;
  if (isDay(deviceToday) && !inWindow(s, deviceToday, DAY_WINDOW - 1)) return null;
  return s;
}
// A chosen day that IS the business today goes back to "follow today".
export function collapseToToday(day, bizToday) {
  return isDay(day) && isDay(bizToday) && day === bizToday ? null : (isDay(day) ? day : null);
}
// R114 review: the Calendar stay panel's "Open Arrivals" hand-off — the
// sessionStorage writes that make Arrivals open on that stay's arrival day.
// A FUTURE arrival pins its day; today / unknown follows today (a pinned date
// would go stale overnight); a stored 'out' filter (which hides arrivals)
// becomes 'in'; 'both' already lists them. focus = the checklist id to open
// expanded (one-shot). Returns { set: {key: value}, remove: [key] }.
// R115 fix: opts.keepPast also pins a PAST day inside the window — the
// Executive Summary drill's deep links (ExecDrill → lib/execDrillLink) open
// "arrived <date>" payment flags on that day; the Calendar keeps future-only.
export function arrivalsHandoff(checkIn, bizToday, curMode, focusId, opts) {
  const keepPast = !!(opts && opts.keepPast);
  const set = { [STORE_KEYS.view]: 'day' };
  const remove = [];
  if (isDay(checkIn) && isDay(bizToday) && (checkIn > bizToday || (keepPast && checkIn < bizToday)) && inWindow(checkIn, bizToday, DAY_WINDOW - 1)) set[STORE_KEYS.day] = checkIn;
  else remove.push(STORE_KEYS.day);
  if (String(curMode || '') === 'out') set[STORE_KEYS.mode] = 'in';
  if (focusId) set[STORE_KEYS.focus] = String(focusId).slice(0, 80);
  return { set, remove };
}
export function parseMode(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return MODES.includes(s) ? s : DEFAULT_MODE;
}
export function parseView(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return VIEWS.includes(s) ? s : DEFAULT_VIEW;
}

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// 'Tue, Oct 13' (UTC-anchored — no locale / timezone drift).
export function longDay(iso) {
  if (!isDay(iso)) return '';
  const d = new Date(iso + 'T00:00:00Z');
  return `${WD[d.getUTCDay()]}, ${MO[d.getUTCMonth()]} ${d.getUTCDate()}`;
}
// { rel: 'Today' | 'Tomorrow' | 'Yesterday' | 'in 8d' | '3d ago', long: 'Tue, Oct 13' }
export function dayLabel(iso, today) {
  const long = longDay(iso);
  if (!isDay(iso) || !isDay(today)) return { rel: '', long };
  const n = diffDays(today, iso);
  const rel = n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : n === -1 ? 'Yesterday' : n > 0 ? `in ${n}d` : `${-n}d ago`;
  return { rel, long };
}

// '4 PM' → 960, '10:30 AM' → 630, anything else → null.
export function timeMinutes(label) {
  const m = /^\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*$/i.exec(String(label == null ? '' : label));
  if (!m) return null;
  let h = Number(m[1]) % 12;
  if (m[3].toUpperCase() === 'PM') h += 12;
  return h * 60 + (m[2] ? Number(m[2]) : 0);
}
export function partyLabel(n) {
  const v = Number(n);
  return n != null && Number.isFinite(v) && v > 0 ? `${v} guest${v === 1 ? '' : 's'}` : '';
}
export function nightsLabel(n) {
  const v = Number(n);
  return n != null && Number.isFinite(v) && v > 0 ? `${v} night${v === 1 ? '' : 's'}` : '';
}

const byListing = (a, b) => String(a.listing_name || '').localeCompare(String(b.listing_name || ''), undefined, { numeric: true, sensitivity: 'base' });
const KIND_ORDER = { departure: 0, arrival: 1 };   // the guest leaves, then the next one arrives
// The rows for a filter tab. in = arrivals, out = departures (server order:
// by time). both = merged and sorted by property, so a same-day pair (the
// checkout and the arrival behind it) sits together.
export function rowsFor(payload, mode) {
  const ins = (payload && Array.isArray(payload.arrivals)) ? payload.arrivals : [];
  const outs = (payload && Array.isArray(payload.departures)) ? payload.departures : [];
  const m = parseMode(mode);
  if (m === 'in') return ins.slice();
  if (m === 'out') return outs.slice();
  return [...outs, ...ins].sort((a, b) => byListing(a, b)
    || ((KIND_ORDER[a.kind] ?? 2) - (KIND_ORDER[b.kind] ?? 2))
    || ((a.time && a.time.minutes != null ? a.time.minutes : 1e6) - (b.time && b.time.minutes != null ? b.time.minutes : 1e6))
    || String(a.hostaway_id || '').localeCompare(String(b.hostaway_id || '')));
}
// Tab counts — the server's counts when present, else the row lengths.
export function modeCounts(payload) {
  const c = (payload && payload.counts) || {};
  const ins = Number.isFinite(c.check_ins) ? c.check_ins : ((payload && payload.arrivals) || []).length;
  const outs = Number.isFinite(c.check_outs) ? c.check_outs : ((payload && payload.departures) || []).length;
  return { in: ins, out: outs, both: ins + outs };
}
export const MODE_LABELS = { in: 'Check-ins', out: 'Check-outs', both: 'Both' };
export function emptyText(mode, longLabel) {
  const m = parseMode(mode);
  const what = m === 'in' ? 'check-ins' : m === 'out' ? 'check-outs' : 'check-ins or check-outs';
  return `No ${what} on ${longLabel || 'this day'}`;
}

const TURN_LABELS = { unstarted: 'Not started', assigned: 'Assigned', in_progress: 'Cleaning', cleaned: 'Cleaned', verified: 'Verified', ready: 'Ready' };
export function turnStatusLabel(status) {
  const s = String(status || '');
  return TURN_LABELS[s] || (s ? s.replace(/_/g, ' ') : 'Not started');
}
const INSP_LABELS = { done: 'Inspected', in_progress: 'Inspecting', assigned: 'Assigned', skipped: 'Skipped', unassigned: 'No inspector', moved: 'Moved', unknown: 'Unknown' };
export function inspectionLabel(state) {
  return INSP_LABELS[String(state || '')] || 'No inspector';
}
// Days for POST /generate to reach a future arrival (generateCore covers
// [today, today+days], capped at 60).
export function generateDays(day, today) {
  if (!isDay(day) || !isDay(today)) return 30;
  return Math.max(1, Math.min(60, diffDays(today, day) + 1));
}
